<template>
  <div class="pre-project-views">
    <!-- Tabs row: scrollable view tabs + right-hand actions. Phones switch views from the page
         title instead (openViewsSheet), unless there's no view yet to switch to. -->
    <div
      v-if="!isPhone || !allViews.length"
      class="pre-project-views__bar"
      :class="{ 'pre-project-views__bar--empty': !allViews.length }"
    >
      <div class="pre-project-views__tabscroll" role="tablist">
        <button
          v-for="view in allViews"
          :key="view.id"
          class="pre-project-views__tab"
          :class="{
            'is-active': view.id === activeViewId,
            'is-dragover': dragOverId === view.id,
            'is-dragging': dragViewId === view.id,
          }"
          role="tab"
          :aria-selected="view.id === activeViewId"
          :draggable="!view.builtIn"
          @click="onSelect(view)"
          @dragstart="onDragStart(view.id, $event)"
          @dragend="onDragEnd"
          @dragover.prevent="dragOverId = view.builtIn ? null : view.id"
          @dragleave="dragOverId = null"
          @drop.prevent="onDrop(view.id)"
        >
          <span v-if="!view.builtIn" class="pre-project-views__grip"><BaseIcon name="grip-vertical" /></span>
          <BaseIcon
            :name="viewIcon(view)"
            class="pre-project-views__tab-ico"
            :class="{ 'is-preset': isDefaultView(view) }"
          />
          <span class="pre-project-views__tab-label">{{ viewLabel(view) }}</span>
        </button>
      </div>

      <div class="pre-project-views__actions">
        <BaseButton ref="allViewsBtn" isSmall isOutlined isSquare hasIconRight @click.native.stop="toggleAllViews">
          <span>{{ $t('page.tenders.views.all-views') }}</span>
          <BaseIcon name="chevron-down" />
        </BaseButton>
        <BaseButton isSmall isSquare hasIconLeft @click.native="onCreate">
          <BaseIcon name="plus" />
          <span>{{ $t('page.tenders.views.add') }}</span>
        </BaseButton>

        <!-- View actions (⋯) menu: edit items are gated by canEdit inside menuItems. -->
        <div v-if="menuItems.length" class="pre-project-views__more">
          <button
            ref="moreBtn"
            type="button"
            class="pre-project-views__more-btn"
            :class="{ 'is-open': moreOpen }"
            :aria-label="$t('page.tenders.views.more-actions')"
            @click.stop="toggleMore"
          >
            <BaseIcon name="more-vertical" />
          </button>
          <div v-if="moreOpen" v-closable="closeMore" class="pre-project-views__moremenu" @click.stop>
            <template v-for="(item, i) in menuItems">
              <div v-if="item.divider" :key="`div-${i}`" class="pre-project-views__moremenu-div"></div>
              <button
                v-else
                :key="item.key"
                type="button"
                class="pre-project-views__moremenu-item"
                :class="{ 'is-danger': item.danger }"
                @click="onMoreItem(item)"
              >
                <span class="pre-project-views__moremenu-ic"><BaseIcon :name="item.icon" /></span>
                <span class="pre-project-views__moremenu-label">{{ item.title }}</span>
                <BaseIcon v-if="item.checked" name="check" class="pre-project-views__moremenu-chk" />
              </button>
            </template>
          </div>
        </div>

        <!-- "All views" dropdown (select + drag-reorder) -->
        <div v-if="allViewsOpen" v-closable="closeAllViews" class="pre-project-views__allviews" @click.stop>
          <div class="pre-project-views__allviews-title">{{ $t('page.tenders.views.reorder-hint') }}</div>
          <div
            v-for="view in allViews"
            :key="view.id"
            class="pre-project-views__allviews-row"
            :class="{ 'is-dragover': dragOverId === view.id, 'is-dragging': dragViewId === view.id }"
            :draggable="!view.builtIn"
            @click="onSelectFromMenu(view)"
            @dragstart="onDragStart(view.id, $event)"
            @dragend="onDragEnd"
            @dragover.prevent="dragOverId = view.builtIn ? null : view.id"
            @dragleave="dragOverId = null"
            @drop.prevent="onDrop(view.id)"
          >
            <!-- Always rendered so every row aligns; stays a hidden spacer (no
                 hover reveal) on the built-in "All" row, whose ordering is disabled. -->
            <span class="pre-project-views__allviews-grip" :class="{ 'is-static': view.builtIn }">
              <BaseIcon name="grip-vertical" />
            </span>
            <BaseIcon
              :name="viewIcon(view)"
              class="pre-project-views__allviews-ico"
              :class="{ 'is-preset': isDefaultView(view) }"
            />
            <span class="pre-project-views__allviews-name">{{ viewLabel(view) }}</span>
            <BaseIcon v-if="view.id === activeViewId" name="check" class="pre-project-views__allviews-check" />
          </div>
        </div>
      </div>
    </div>

    <!-- Filters go between the tabs and the view meta -->
    <div class="pre-project-views__filters">
      <slot name="filters" />
    </div>

    <!-- Active view meta: scope badges (left), dirty save-state (right). Phones show the badge in
         the views sheet, and this row only for unsaved changes. -->
    <div v-if="activeView && (!isPhone || isActiveViewDirty)" class="pre-project-views__meta">
      <div v-if="!isPhone" class="pre-project-views__badges">
        <BaseTag :tone="badge.tone" :icon="badge.icon" preset="soft">{{ badge.text }}</BaseTag>
      </div>

      <!-- Dirty save-state: working filters differ from the saved view -->
      <div v-if="isActiveViewDirty" class="pre-project-views__save">
        <span class="pre-project-views__dirty-note">{{ $t('page.tenders.views.modified') }}</span>
        <BaseButton isSmall isOutlined isSquare @click.native="onReset">
          {{ $t('page.tenders.views.reset') }}
        </BaseButton>
        <!-- Phones: one button, the two ways to save in a sheet. -->
        <BaseButton v-if="isPhone" isSmall isSquare @click.native="saveSheetOpen = true">
          {{ $t('page.tenders.views.save') }}
        </BaseButton>
        <template v-else>
          <!-- Save-as-new is secondary while the view is editable, primary when Save changes is locked. -->
          <BaseButton isSmall isSquare :isOutlined="canEditActiveView" @click.native="openSaveAsNew">
            {{ $t('page.tenders.views.save-as-new') }}
          </BaseButton>
          <BaseButton
            class="pre-project-views__save-changes"
            :class="{ 'is-locked': !canEditActiveView }"
            isSmall
            isSquare
            :disabled="!canEditActiveView"
            :tooltipText="updateDisabledReason"
            @click.native="onUpdate"
          >
            {{ $t('page.tenders.views.update') }}
          </BaseButton>
        </template>
      </div>
    </div>

    <slot name="search" />

    <!-- Phones: every view, a new one, and what can be done with the one shown. -->
    <BaseBottomSheet v-if="isPhone" v-model="viewsSheetOpen" :title="$t('page.tenders.views.sheet-title')">
      <ul class="base-bottom-sheet__menu">
        <li v-for="view in allViews" :key="view.id">
          <button
            type="button"
            :class="{ 'is-active': view.id === activeViewId }"
            :aria-current="view.id === activeViewId ? 'true' : null"
            @click="fromViewsSheet(() => onSelect(view))"
          >
            <BaseIcon
              :name="viewIcon(view)"
              class="pre-project-views__sheet-ico"
              :class="{ 'is-preset': isDefaultView(view) }"
            />
            <span class="pre-project-views__sheet-name">{{ viewLabel(view) }}</span>
            <BaseIcon v-if="view.id === activeViewId" name="check" class="base-bottom-sheet__menu-check" />
          </button>
        </li>
        <li>
          <button type="button" @click="fromViewsSheet(onCreate)">
            <BaseIcon name="plus" />
            {{ $t('page.tenders.views.add') }}
          </button>
        </li>
      </ul>
      <template v-if="activeView">
        <div class="pre-project-views__sheet-current">
          <p class="pre-project-views__sheet-heading">{{ viewLabel(activeView) }}</p>
          <div class="pre-project-views__badges">
            <BaseTag :tone="badge.tone" :icon="badge.icon" preset="soft">{{ badge.text }}</BaseTag>
          </div>
        </div>
        <ul class="base-bottom-sheet__menu">
          <li v-for="item in sheetMenuItems" :key="item.key">
            <button type="button" :class="{ 'is-danger': item.danger }" @click="fromViewsSheet(item.onClick)">
              <BaseIcon :name="item.icon" />
              {{ item.title }}
            </button>
          </li>
        </ul>
      </template>
    </BaseBottomSheet>

    <BaseBottomSheet v-if="isPhone" v-model="saveSheetOpen" :title="$t('page.tenders.views.modified')">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" :disabled="!canEditActiveView" @click="fromSaveSheet(onUpdate)">
            <BaseIcon name="save" />
            {{ $t('page.tenders.views.update') }}
          </button>
          <p v-if="!canEditActiveView" class="base-bottom-sheet__menu-note">{{ updateDisabledReason }}</p>
        </li>
        <li>
          <button type="button" @click="fromSaveSheet(openSaveAsNew)">
            <BaseIcon name="plus" />
            {{ $t('page.tenders.views.save-as-new') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>

    <!-- Rename / Create / Save-as-new dialog -->
    <BaseDialog
      centered
      ref="formDialog"
      contentClass="pre-project-view-dialog"
      :show-actions="true"
      @closeModal="closeForm"
      :onClose="closeForm"
    >
      <template slot="title">{{ formTitle }}</template>
      <template slot="content">
        <div class="pre-project-views__field">
          <label class="pre-project-views__field-label">{{ $t('page.tenders.views.name-label') }}</label>
          <FormInput
            ref="nameInput"
            name="view-name"
            :maxlength="viewNameMaxLength"
            :value="form.name"
            :placeholder="$t('page.tenders.views.name-placeholder')"
            @input="form.name = $event"
            @keyup.native.enter="submitForm"
          />
        </div>

        <template v-if="form.mode !== 'rename'">
          <div class="pre-project-views__field">
            <label class="pre-project-views__field-label">{{ $t('page.tenders.views.visibility') }}</label>
            <div class="pre-project-views__radiorow">
              <button
                type="button"
                class="pre-project-views__radiocard"
                :class="{ 'is-on': form.scope === PRIVATE }"
                @click="form.scope = PRIVATE"
              >
                <span class="pre-project-views__radiocard-title">
                  <BaseIcon name="user" />{{ $t('page.tenders.views.scope-private') }}
                </span>
                <span class="pre-project-views__radiocard-desc">
                  {{ $t('page.tenders.views.scope-private-desc') }}
                </span>
              </button>
              <button
                type="button"
                class="pre-project-views__radiocard"
                :class="{ 'is-on': form.scope === SHARED }"
                @click="form.scope = SHARED"
              >
                <span class="pre-project-views__radiocard-title">
                  <BaseIcon name="users" />{{ $t('page.tenders.views.scope-shared') }}
                </span>
                <span class="pre-project-views__radiocard-desc">
                  {{ $t('page.tenders.views.scope-shared-desc') }}
                </span>
              </button>
            </div>
          </div>

          <div v-if="form.mode === 'save-as-new'" class="pre-project-views__note">
            {{ $t('page.tenders.views.filters-note') }}
          </div>
        </template>
      </template>
      <template slot="actions">
        <div class="pre-project-views__dialog-actions">
          <BaseButton isOutlined isSquare @click.native="closeForm">{{ $t('page.tenders.views.cancel') }}</BaseButton>
          <BaseButton :disabled="!form.name.trim()" @click.native="submitForm">{{ formSubmitLabel }}</BaseButton>
        </div>
      </template>
    </BaseDialog>

    <!-- Delete confirmation -->
    <BaseDialog
      centered
      ref="deleteDialog"
      contentClass="pre-project-view-confirm"
      :show-actions="true"
      @closeModal="closeDelete"
      :onClose="closeDelete"
    >
      <template slot="title">{{ $t('page.tenders.views.delete-title') }}</template>
      <template slot="content">
        <p class="pre-project-views__confirm-text">
          <i18n path="page.tenders.views.delete-confirm" tag="span">
            <template v-slot:name>
              <strong>{{ confirmDelete.name }}</strong>
            </template>
          </i18n>
          <br />
          {{
            confirmDelete.shared
              ? $t('page.tenders.views.delete-shared-warning')
              : $t('page.tenders.views.delete-irreversible')
          }}
        </p>
      </template>
      <template slot="actions">
        <div class="pre-project-views__dialog-actions">
          <BaseButton isOutlined isSquare @click.native="closeDelete">
            {{ $t('page.tenders.views.cancel') }}
          </BaseButton>
          <BaseButton isDanger @click.native="confirmDeleteView">
            {{ $t('page.tenders.views.delete-action') }}
          </BaseButton>
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import FormInput from '@/components/form/FormInput.vue';
import { VIEW_NAME_MAX_LENGTH, VIEW_SCOPE } from '@/store/pre-project-views.module';
import { isPresetView, viewIconName } from '@/models/tender-views';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'PreProjectViews',
  components: { BaseBottomSheet, BaseButton, BaseDialog, BaseIcon, BaseTag, FormInput },
  data() {
    return {
      allViewsOpen: false,
      moreOpen: false,
      // Phones' sheets: the views, and the two ways to save changes.
      viewsSheetOpen: false,
      saveSheetOpen: false,
      // Drag-reorder state (shared by the tabs and the all-views dropdown).
      dragViewId: null,
      dragOverId: null,
      // Rename / create / save-as-new form dialog state.
      form: {
        mode: null, // 'rename' | 'create' | 'save-as-new'
        name: '',
        scope: VIEW_SCOPE.PRIVATE,
      },
      // Delete confirmation dialog state.
      confirmDelete: {
        name: '',
        shared: false,
      },
    };
  },
  computed: {
    ...mapGetters('preProjectViews', [
      'allViews',
      'activeView',
      'activeViewId',
      'isActiveViewDirty',
      'canEditActiveView',
    ]),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['users']),
    viewNameMaxLength() {
      return VIEW_NAME_MAX_LENGTH;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    // The active view's scope.
    badge() {
      const t = (key) => this.$t(`page.tenders.views.${key}`);
      const view = this.activeView;
      // Preset: locked default (backend preset or the built-in "All" tab). Icon matches the tab
      // (bookmark for Saved, list for All, lock otherwise).
      if (view && (view.preset || view.builtIn)) {
        return { tone: 'warning', icon: this.activePresetBadgeIcon, text: t('preset') };
      }
      // Shared view owned by a teammate (shared TO you).
      if (this.isSharedWithMe) return { tone: 'info', icon: 'users', text: this.sharedByLabel };
      // Your own shared view.
      if (this.isShared) return { tone: 'success', icon: 'users', text: t('scope-shared') };
      // Private: personal to the owner.
      return { tone: 'neutral', icon: 'user', text: t('scope-private') };
    },
    SHARED() {
      return VIEW_SCOPE.SHARED;
    },
    PRIVATE() {
      return VIEW_SCOPE.PRIVATE;
    },
    isShared() {
      return !!this.activeView && this.activeView.scope === VIEW_SCOPE.SHARED;
    },
    // A shared view owned by someone else (shared TO the current user).
    isSharedWithMe() {
      const view = this.activeView;
      return !!(
        view &&
        !view.preset &&
        view.scope === VIEW_SCOPE.SHARED &&
        view.ownerUserId &&
        this.currentUser &&
        view.ownerUserId !== this.currentUser.id
      );
    },
    // "Partagée par <owner>" when we can resolve the owner, else a generic "shared with you".
    sharedByLabel() {
      if (!this.activeView) return '';
      const owner = (this.users || []).find((user) => user.id === this.activeView.ownerUserId);
      const name = owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : '';
      return name ? this.$t('page.tenders.views.shared-by', { name }) : this.$t('page.tenders.views.shared-with-you');
    },
    // Why "Save changes" is disabled — a locked preset/built-in vs a view shared with you.
    updateDisabledReason() {
      if (this.canEditActiveView) return '';
      const preset = this.activeView && (this.activeView.preset || this.activeView.builtIn);
      return this.$t(`page.tenders.views.${preset ? 'update-disabled-preset' : 'update-disabled-shared'}`);
    },
    activePresetBadgeIcon() {
      if (!this.activeView) return 'lock';
      if (this.activeView.builtIn) return 'list';
      return viewIconName(this.activeView);
    },
    formTitle() {
      const t = (key) => this.$t(`page.tenders.views.${key}`);
      if (this.form.mode === 'rename') return t('rename-title');
      if (this.form.mode === 'save-as-new') return t('save-as-new-title');
      return t('create-title');
    },
    formSubmitLabel() {
      return this.$t(`page.tenders.views.${this.form.mode === 'rename' ? 'rename-action' : 'create-action'}`);
    },
    menuItems() {
      if (!this.activeView) return [];
      const t = (key) => this.$t(`page.tenders.views.${key}`);
      const canEdit = this.canEditActiveView;
      const items = [];
      if (canEdit) items.push({ key: 'rename', title: t('rename'), icon: 'edit-2', onClick: this.openRename });
      items.push({ key: 'duplicate', title: t('duplicate'), icon: 'copy', onClick: this.onDuplicate });
      if (canEdit) {
        items.push({
          key: 'scope',
          title: this.isShared ? t('make-private') : t('share'),
          icon: 'share-2',
          checked: this.isShared,
          onClick: this.onToggleScope,
        });
        items.push({ divider: true });
        items.push({ key: 'delete', title: t('delete'), icon: 'trash-2', danger: true, onClick: this.onDelete });
      }
      return items;
    },
    sheetMenuItems() {
      return this.menuItems.filter((item) => !item.divider);
    },
  },
  methods: {
    ...mapActions('preProjectViews', [
      'updateActiveView',
      'createView',
      'saveAsNewView',
      'renameView',
      'toggleScope',
      'duplicateView',
      'removeView',
      'resetActiveView',
      'reorder',
    ]),
    // Seeded default views carry an i18n nameKey; translate it, else show the typed name.
    viewLabel(view) {
      return view.nameKey ? this.$t(view.nameKey) : view.name;
    },
    // Icon per view kind: built-in "All" → list, saved → bookmark, shared → share,
    // other presets → lock, shared user views → group, private → person.
    viewIcon(view) {
      if (!view) return 'user';
      if (view.builtIn) return 'list';
      return viewIconName(view);
    },
    isDefaultView(view) {
      if (!view || view.builtIn) return false;
      return isPresetView(view);
    },
    onSelect(view) {
      if (view.id === this.activeViewId) return;
      this.$emit('select', view);
    },
    onSelectFromMenu(view) {
      this.closeAllViews();
      this.onSelect(view);
    },
    toggleAllViews() {
      this.allViewsOpen = !this.allViewsOpen;
      this.moreOpen = false;
    },
    // Ignore the outside-mousedown when it originates from this dropdown's own
    // activator — otherwise v-closable (mousedown) closes it and the activator's
    // click immediately re-toggles it open.
    closeAllViews(event) {
      const btn = this.$refs.allViewsBtn;
      if (event && btn && btn.$el.contains(event.target)) return;
      this.allViewsOpen = false;
    },
    toggleMore() {
      this.moreOpen = !this.moreOpen;
      this.allViewsOpen = false;
    },
    closeMore(event) {
      const btn = this.$refs.moreBtn;
      if (event && btn && btn.contains(event.target)) return;
      this.moreOpen = false;
    },
    onMoreItem(item) {
      this.closeMore();
      item.onClick();
    },
    // ---- phones ----
    openViewsSheet() {
      this.viewsSheetOpen = true;
    },
    // A choice in a sheet closes it first: what follows may open a dialog of its own.
    fromViewsSheet(action) {
      this.viewsSheetOpen = false;
      action();
    },
    fromSaveSheet(action) {
      this.saveSheetOpen = false;
      action();
    },
    // ---- drag-reorder ----
    // (the built-in "All" stays first: not draggable, and dropping on it
    // no-ops since it's absent from the ordered id list)
    onDragStart(viewId, event) {
      this.dragViewId = viewId;
      if (event.dataTransfer) event.dataTransfer.setData('text/plain', viewId);
    },
    onDragEnd() {
      this.dragViewId = null;
      this.dragOverId = null;
    },
    onDrop(targetId) {
      const fromId = this.dragViewId;
      this.dragOverId = null;
      this.dragViewId = null;
      if (!fromId || fromId === targetId) return;
      // Order only the API views — "All" is client-side and excluded from the list.
      const ids = this.allViews.filter((view) => !view.builtIn).map((view) => view.id);
      const from = ids.indexOf(fromId);
      const to = ids.indexOf(targetId);
      if (from < 0 || to < 0) return;
      // The dragged view takes the target's slot; the target shifts away from the drop
      // direction (forward drop lands after the target, backward lands before) so either end
      // stays reachable.
      ids.splice(to, 0, ids.splice(from, 1)[0]);
      this.reorder(ids).catch(() => this.toast('error', 'action-error'));
    },
    // ---- view actions ----
    onCreate() {
      this.openForm('create');
    },
    onUpdate() {
      // The button is disabled on uneditable views but stays clickable-through (pointer-events:none
      // lets its tooltip show), so guard here too — clicking it must not save/toast.
      if (!this.canEditActiveView) return;
      this.updateActiveView()
        .then(() => this.toast('success', 'update-toast'))
        .catch(() => this.toast('error', 'action-error'));
    },
    onReset() {
      this.resetActiveView().then(() => this.$emit('refresh'));
    },
    onToggleScope() {
      // Capture the direction before dispatching — the store flips view.scope in place.
      const sharing = this.activeView.scope !== VIEW_SCOPE.SHARED;
      this.toggleScope(this.activeViewId)
        .then(() => this.toast('success', sharing ? 'shared-toast' : 'private-toast'))
        .catch(() => this.toast('error', 'action-error'));
    },
    // Confirmation feedback for view actions whose only other cue is a quiet badge change.
    toast(kind, key) {
      if (this.$toast) this.$toast[kind](this.$t(`page.tenders.views.${key}`));
    },
    onDuplicate() {
      this.duplicateView(this.activeViewId)
        .then(() => {
          this.$emit('refresh');
          this.toast('success', 'duplicate-toast');
        })
        .catch(() => this.toast('error', 'action-error'));
    },
    onDelete() {
      this.confirmDelete = { name: this.viewLabel(this.activeView), shared: this.isShared };
      this.$refs.deleteDialog.openModal();
    },
    closeDelete() {
      if (this.$refs.deleteDialog) this.$refs.deleteDialog.isModalOpen = false;
    },
    confirmDeleteView() {
      this.removeView(this.activeViewId)
        .then(() => {
          // The store falls back to the built-in "All" view; re-run the search.
          this.$emit('refresh');
          this.toast('success', 'delete-toast');
        })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message) || null;
          if (msg && this.$toast) this.$toast.error(msg);
          else this.toast('error', 'action-error');
        });
      this.closeDelete();
    },
    openRename() {
      this.openForm('rename', { name: this.activeView.name });
    },
    // Called from the dirty save-bar: persist the working filters as a brand-new view.
    openSaveAsNew() {
      this.openForm('save-as-new');
    },
    openForm(mode, { name } = {}) {
      // Start blank for create/save-as-new so the placeholder shows and a meaningful name is
      // required (submit stays disabled until typed). Rename passes the current name in.
      this.form = { mode, name: name || '', scope: VIEW_SCOPE.PRIVATE };
      this.$refs.formDialog.openModal();
      this.focusNameInput();
    },
    // Focus the name field on open. The dialog grabs focus during its slide-in transition, so
    // focus at a few points and let the last (post-transition) call win.
    focusNameInput() {
      const focus = () => {
        const wrapper = this.$refs.nameInput;
        const input = wrapper && wrapper.$el && wrapper.$el.querySelector('input');
        if (input) {
          input.focus();
          input.select();
        }
      };
      [0, 180, 350].forEach((d) => setTimeout(focus, d));
    },
    closeForm() {
      if (this.$refs.formDialog) this.$refs.formDialog.isModalOpen = false;
    },
    submitForm() {
      // maxlength caps typing/paste; clamp here too so a programmatically set value can't be
      // saved over the DB cap.
      const name = this.form.name.trim().slice(0, VIEW_NAME_MAX_LENGTH);
      if (!name) return;
      // Capture before the async resolution — the dialog closes (and the form may
      // be reopened) before the request lands.
      const mode = this.form.mode;
      if (mode === 'rename') {
        this.renameView({ viewId: this.activeView.id, name })
          .then(() => this.toast('success', 'rename-toast'))
          .catch(() => this.toast('error', 'action-error'));
      } else {
        const create = mode === 'save-as-new' ? this.saveAsNewView : this.createView;
        create({ name, scope: this.form.scope })
          .then(() => {
            this.$emit('refresh');
            this.toast('success', mode === 'save-as-new' ? 'save-as-new-toast' : 'create-toast');
          })
          .catch(() => this.toast('error', 'action-error'));
      }
      this.closeForm();
    },
  },
};
</script>

<style lang="scss">
.pre-project-views {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid $color-border;

    // No views yet → no tabs to separate, so drop the divider line.
    &--empty {
      border-bottom: none;
    }
  }

  &__tabscroll {
    display: flex;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // Mirrors .tender-views__tab.
  &__tab {
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    color: $color-slate-500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 14px;
    font-weight: bold;
    padding: 9px 13px 11px;
    white-space: nowrap;
    border-radius: $border-radius-root $border-radius-root 0 0;
    transition: color ease 100ms, background ease 100ms;

    &:hover {
      color: $color-normal;
      background: $color-hover;
    }

    &.is-active {
      color: $color-accent;
      border-bottom-color: $color-accent;
    }

    &.is-dragover {
      background: rgba($color-accent-500, 0.1);
    }

    &.is-dragging {
      opacity: 0.4;
    }

    &:hover .pre-project-views__grip {
      visibility: visible;
    }
  }

  // Drag-reorder grip: hidden until the tab is hovered.
  &__grip {
    color: $color-slate-300;
    cursor: grab;
    display: inline-flex;
    visibility: hidden;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  // Mirrors .tender-views__tab-ico (presets tinted amber).
  &__tab-ico {
    width: 13px;
    height: 13px;
    flex: none;
    color: $color-slate-400;

    &.is-preset {
      color: $color-semantic-warning;
    }
  }

  &__actions {
    position: relative;
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 6px;
    flex: none;
  }

  // ---- view actions (⋯) menu ----
  &__more {
    position: relative;
  }

  &__more-btn {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    border-radius: $radius-md;
    border: none;
    background: none;
    color: $color-slate-500;
    cursor: pointer;
    transition: background ease 100ms, color ease 100ms;

    .feather {
      width: 18px;
      height: 18px;
    }

    &:hover,
    &.is-open {
      background: $color-hover;
      color: $color-normal;
    }
  }

  &__moremenu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 40;
    min-width: 240px;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    box-shadow: $shadow-dropdown;
    padding: 8px;
  }

  &__moremenu-item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 8px 9px;
    border: none;
    background: none;
    border-radius: $radius-sm;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;
    text-align: left;

    &:hover {
      background: $color-hover;
    }

    &.is-danger {
      color: $color-red-700;
    }
  }

  &__moremenu-ic {
    display: grid;
    place-items: center;
    width: 16px;
    color: $color-slate-400;

    .feather {
      width: 15px;
      height: 15px;
    }
  }

  &__moremenu-item.is-danger &__moremenu-ic {
    color: $color-red-700;
  }

  &__moremenu-label {
    flex: 1;
  }

  &__moremenu-chk {
    width: 15px;
    height: 15px;
    color: $color-accent;
    flex: none;
  }

  &__moremenu-div {
    height: 1px;
    background: $color-border;
    margin: 6px 2px;
  }

  // ---- "all views" dropdown ----
  &__allviews {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 40;
    min-width: 260px;
    max-height: 360px;
    overflow-y: auto;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    box-shadow: $shadow-dropdown;
    padding: 6px;
  }

  &__allviews-title {
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-slate-400;
    margin: 6px 8px;
  }

  &__allviews-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 9px;
    border-radius: $border-radius-small;
    cursor: pointer;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;

    &:hover {
      background: $color-hover;
    }

    &.is-dragover {
      background: rgba($color-accent-500, 0.1);
    }

    &.is-dragging {
      opacity: 0.4;
    }

    &:hover .pre-project-views__allviews-grip:not(.is-static) {
      visibility: visible;
    }
  }

  &__allviews-grip {
    color: $color-slate-300;
    cursor: grab;
    display: inline-flex;
    visibility: hidden;
    flex: none;

    svg {
      width: 13px;
      height: 13px;
    }

    // Alignment spacer on the built-in "All" row — never revealed, not draggable.
    &.is-static {
      cursor: default;
    }
  }

  &__allviews-ico {
    width: 15px;
    height: 15px;
    color: $color-slate-400;
    flex: none;

    &.is-preset {
      color: $color-semantic-warning;
    }
  }

  &__allviews-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__allviews-check {
    width: 15px;
    height: 15px;
    color: $color-accent;
    flex: none;
  }

  // ---- dialogs ----
  &__field {
    margin-bottom: 16px;
  }

  &__field-label {
    display: block;
    font-size: 12px;
    font-weight: 800;
    color: $color-normal;
    margin-bottom: 6px;
  }

  // FormInput carries a form-layout label/padding scaffold that we don't need inside the modal.
  &__field .form-input {
    padding: 0;
  }

  &__radiorow {
    display: flex;
    gap: 10px;
  }

  &__radiocard {
    flex: 1;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    padding: 12px;
    cursor: pointer;
    background: $color-white;
    text-align: left;
    font-family: inherit;

    &.is-on {
      border-color: $color-accent;
      background: $color-accent-100;
    }
  }

  &__radiocard-title {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 800;
    color: $color-normal;

    .feather {
      width: 15px;
      height: 15px;
    }
  }

  &__radiocard-desc {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: $color-slate-500;
  }

  &__note {
    margin-top: 6px;
    padding: 11px 13px;
    background: $color-hover;
    border-radius: $border-radius-root;
    font-size: 12px;
    color: $color-slate-500;
  }

  &__confirm-text {
    font-size: 14px;
    color: $color-text-body;
    margin: 0;
  }

  &__dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  // ---- active view meta (badges + dirty save-state), mirrors .tender-views__meta ----
  &__meta {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 28px;
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: 9px;
  }

  &__save {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // When Save-changes is disabled (uneditable view), let hover fall through the inert <button> to
  // the wrapper so BaseButton's explanatory tooltip still appears (disabled buttons eat hover).
  &__save-changes {
    &.is-locked {
      cursor: not-allowed;
    }

    .base-button__element:disabled {
      pointer-events: none;
    }
  }

  &__dirty-note {
    font-size: 12px;
    font-weight: bold;
    color: $color-semantic-warning;

    // Phones: the note takes what the two buttons leave.
    @include screen-below('tablet') {
      flex: 1;
    }
  }

  // Phones: the unsaved-changes row runs the width of the screen, as in the tenders inbox.
  @include screen-below('tablet') {
    &__save {
      flex: 1 1 100%;
      margin-left: 0;
    }
  }

  // ---- phones' views sheet ----
  &__sheet-ico.is-preset {
    color: $color-semantic-warning;
  }

  &__sheet-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The view shown: its badge says what the menu's toggles are set to.
  &__sheet-current {
    margin: 8px 0 4px;
    padding-top: 16px;
    border-top: 1px solid $color-border;
  }

  &__sheet-heading {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: bold;
    color: $color-slate-500;
  }
}

// Center these BaseDialog instances (the base component defaults to a right-side drawer)
// by overriding position to relative — same approach as the tender-views dialogs.
.base-dialog__pre-project-view-dialog,
.base-dialog__pre-project-view-confirm {
  position: relative;
  top: 0;
  height: auto;
  width: 460px;
  max-width: 90vw;

  .base-dialog__header {
    height: auto;
    border-bottom: 1px solid $color-border;

    .header__title {
      font-size: 18px;
      margin: 18px 22px;
    }
  }

  .base-dialog__header + .base-dialog__content {
    padding: 20px 22px;
    background: $color-white;
  }

  .base-dialog__actions {
    padding: 16px 22px;
    border-top: 1px solid $color-border;
  }
}
</style>
